/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { appRoutes } from 'app/routes';
import { compileMenu } from 'app/core/utils/menu';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { usePoolInvestProjects } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { Dashboard } from './dashboard';
import { ArrowLeftRight, CalendarClock, Folder, Layers, PieChart, Users } from 'lucide-react';

/**
 * Export `SkinPoolInvest` component.
 *
 * Section shell for Pool Invest — shared ("pooled") investment tracking. The
 * section menu shows the overview plus one entry per project (mirrors Projects).
 */

export function SkinPoolInvest(): JSX.Element {
  const [translate] = useTranslation();
  const { data } = usePoolInvestProjects();
  const items = useMemo(
    () =>
      compileMenu([
        {
          key: 'overview',
          pathname: appRoutes.dashboard.poolInvest.base,
          icon: <PieChart className={'h-4 w-4'} />,
          label: translate('poolInvest.menu.overview')
        },
        ...(data?.map(project => ({
          key: `project_${project.id}`,
          pathname: undefined,
          icon: <Folder className={'h-4 w-4'} />,
          label: project.name,
          children: [
            {
              key: `${project.id}_overview`,
              pathname: resolveAppPath(appRoutes.dashboard.poolInvest.project, { projectId: project.id }),
              icon: <PieChart className={'h-4 w-4'} />,
              label: translate('poolInvest.menu.overview')
            },
            {
              key: `${project.id}_users`,
              pathname: resolveAppPath(appRoutes.dashboard.poolInvest.projectUsers, { projectId: project.id }),
              icon: <Users className={'h-4 w-4'} />,
              label: translate('poolInvest.menu.users')
            },
            {
              key: `${project.id}_rules`,
              pathname: resolveAppPath(appRoutes.dashboard.poolInvest.projectRules, { projectId: project.id }),
              icon: <CalendarClock className={'h-4 w-4'} />,
              label: translate('poolInvest.menu.rules')
            },
            {
              key: `${project.id}_categories`,
              pathname: resolveAppPath(appRoutes.dashboard.poolInvest.projectCategories, { projectId: project.id }),
              icon: <Layers className={'h-4 w-4'} />,
              label: translate('poolInvest.menu.categories')
            },
            {
              key: `${project.id}_transactions`,
              pathname: resolveAppPath(appRoutes.dashboard.poolInvest.projectTransactions, { projectId: project.id }),
              icon: <ArrowLeftRight className={'h-4 w-4'} />,
              label: translate('poolInvest.menu.transactions')
            }
          ]
        })) ?? [])
      ]),
    [data, translate]
  );

  return (
    <Dashboard menu={items}>
      <Outlet />
    </Dashboard>
  );
}
